/**
 * User Data Layer initialization
 * Sets user attributes before GTM loads
 * 
 * Per client specification:
 * - Push user_data before GTM script loads
 * - Use undefined for unavailable fields (not null)
 * - logged_in should be boolean (false if not logged in)
 * - user_id must be non-identifiable and persistent
 * - Re-push on login/logout during session
 */

/**
 * Initialize user data in dataLayer
 * Should be called before GTM script loads
 * 
 * @param {Object} params
 * @param {boolean} params.logged_in - Whether user is logged in (true/false)
 * @param {string} params.user_id - Non-identifiable persistent user ID
 * @param {string|number} params.membership_status - Membership status/level
 */
export function initializeUserData({ logged_in = false, user_id, membership_status } = {}) {
  if (typeof window === 'undefined') return;

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Build user_data object - only include fields that have values
  const userData = {
    logged_in: logged_in
  };

  // Add user_id only if available (undefined if not)
  if (user_id) {
    userData.user_id = user_id;
  } else {
    userData.user_id = undefined;
  }

  // Add membership_status only if available (undefined if not)
  if (membership_status) {
    userData.membership_status = membership_status;
  } else {
    userData.membership_status = undefined;
  }

  // Push user data with newTracking flag
  window.dataLayer.push({
    user_data: userData,
    newTracking: true
  });
}

/**
 * Update user data in dataLayer
 * Called when user logs in/out or membership changes during session
 * 
 * @param {Object} params
 * @param {boolean} params.logged_in - Whether user is logged in
 * @param {string} params.user_id - User ID
 * @param {string|number} params.membership_status - Membership status
 */
export function updateUserData({ logged_in, user_id, membership_status } = {}) {
  initializeUserData({ logged_in, user_id, membership_status });
}

